/**
 * Kebab menu for project cards/rows
 * Story 464: Export full project data as a downloadable zip of markdown files
 *
 * Appears on hover, provides "Export..." action.
 * Uses stopPropagation to prevent parent card/row navigation.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import './ProjectActions.css';

interface ProjectActionsProps {
  projectSlug: string;
  onExport: (slug: string) => void;
}

export function ProjectActions({ projectSlug, onExport }: ProjectActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(prev => !prev);
  }, []);

  const handleExportClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    onExport(projectSlug);
  }, [projectSlug, onExport]);

  return (
    <div
      ref={menuRef}
      className="project-actions"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="project-actions__trigger"
        onClick={handleTriggerClick}
        aria-label="Project actions"
        aria-expanded={isOpen}
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {isOpen && (
        <div className="project-actions__menu" role="menu">
          <button
            className="project-actions__item"
            onClick={handleExportClick}
            role="menuitem"
            type="button"
          >
            Export...
          </button>
        </div>
      )}
    </div>
  );
}
